import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import './TimesheetPage.css';
import { toast } from '../components/Toast';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function DetailModal({ timesheet, onClose, onApprove, onReject, processing }) {
  const [comment, setComment] = useState('');

  if (!timesheet) return null;

  return (
    <div className="ts-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ts-modal">
        <div className="ts-modal-header">
          <h3>Timesheet Detail</h3>
          <button className="ts-close" onClick={onClose}>&times;</button>
        </div>

        <div className="ts-modal-body">
          {/* Employee & Date */}
          <div className="ts-detail-grid">
            <div className="ts-detail-item">
              <span className="ts-detail-label">Employee</span>
              <span className="ts-detail-value"><strong>{timesheet.employeeName || '—'}</strong></span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Work Date</span>
              <span className="ts-detail-value">{fmt(timesheet.workDate)}</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Hours Worked</span>
              <span className="ts-detail-value ts-hours">{timesheet.hoursWorked}h</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Time</span>
              <span className="ts-detail-value">
                {timesheet.startTime ? `${timesheet.startTime.slice(0,5)} – ${timesheet.endTime?.slice(0,5) || '?'}` : '—'}
              </span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Project</span>
              <span className="ts-detail-value">{timesheet.projectName || '—'}</span>
            </div>
            <div className="ts-detail-item">
              <span className="ts-detail-label">Submitted</span>
              <span className="ts-detail-value">{fmt(timesheet.createdAt)}</span>
            </div>
          </div>

          {/* Full description */}
          <div className="ts-detail-section">
            <span className="ts-detail-label">Description</span>
            <div className="ts-detail-desc">
              {timesheet.description || <span className="ts-muted">No description provided</span>}
            </div>
          </div>

          {/* Existing comment */}
          {timesheet.reviewComment && (
            <div className="ts-detail-section">
              <span className="ts-detail-label">Previous Review Comment</span>
              <div className="ts-detail-desc ts-comment-existing">{timesheet.reviewComment}</div>
            </div>
          )}

          {/* Add comment */}
          <div className="ts-detail-section">
            <label className="ts-detail-label">Add Review Comment (optional)</label>
            <textarea
              className="ts-comment-input"
              rows={3}
              placeholder="Add a comment for the employee…"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>
        </div>

        <div className="ts-modal-footer">
          <button className="ts-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="ts-reject-btn"
            onClick={() => onReject(timesheet.id, comment)}
            disabled={processing === timesheet.id}
          >
            {processing === timesheet.id ? '…' : '✗ Reject'}
          </button>
          <button
            className="ts-approve-btn"
            onClick={() => onApprove(timesheet.id, comment)}
            disabled={processing === timesheet.id}
          >
            {processing === timesheet.id ? '…' : '✓ Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TimesheetApprovals() {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/timesheets/pending-approval');
      const data = res.data?.timesheets || res.data?.data || [];
      setTimesheets(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Access denied. Please log out and log back in.');
      } else {
        toast.error(e.response?.data?.message || 'Failed to load pending timesheets');
      }
    } finally { setLoading(false); }
  };

  const handleApprove = async (id, comment) => {
    setProcessing(id);
    try {
      await axios.post(`/timesheets/${id}/approve`, { comment: comment || null });
      flash('Timesheet approved');
      setSelected(null);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve');
    } finally { setProcessing(null); }
  };

  const handleReject = async (id, comment) => {
    setProcessing(id);
    try {
      await axios.post(`/timesheets/${id}/reject`, { comment: comment || null });
      flash('Timesheet returned to employee');
      setSelected(null);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject');
    } finally { setProcessing(null); }
  };

  const flash = (msg) => { toast.success(msg); };

  return (
    <div className="ts-container">
      <div className="ts-header">
        <div>
          <h1 className="ts-title">Timesheet Approvals</h1>
          <p className="ts-subtitle">Review and approve pending timesheet submissions</p>
        </div>
        <button className="ts-btn-secondary" onClick={fetchPending}>↻ Refresh</button>
      </div>

      <div className="ts-summary">
        <div className="ts-stat">
          <span className="ts-stat-value ts-pending">{timesheets.length}</span>
          <span className="ts-stat-label">Pending</span>
        </div>
      </div>

      {loading ? (
        <div className="ts-loading">Loading…</div>
      ) : timesheets.length === 0 ? (
        <div className="ts-empty">
          <div className="ts-empty-icon">✅</div>
          <p>No pending timesheets — all caught up!</p>
        </div>
      ) : (
        <div className="ts-table-wrap">
          <table className="ts-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Date</th>
                <th>Hours</th>
                <th>Project</th>
                <th>Description</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map(t => (
                <tr key={t.id} className="ts-clickable-row" onClick={() => setSelected(t)}>
                  <td data-label="Employee"><strong>{t.employeeName || '—'}</strong></td>
                  <td data-label="Date">{fmt(t.workDate)}</td>
                  <td data-label="Hours"><span className="ts-hours">{t.hoursWorked}h</span></td>
                  <td data-label="Project">{t.projectName || <span className="ts-muted">—</span>}</td>
                  <td data-label="Description" className="ts-desc-preview">
                    {t.description
                      ? (t.description.length > 60 ? t.description.slice(0, 60) + '…' : t.description)
                      : <span className="ts-muted">—</span>}
                  </td>
                  <td data-label="Submitted" className="ts-muted" style={{ fontSize: '12px' }}>{fmt(t.createdAt)}</td>
                  <td data-label="Actions" onClick={e => e.stopPropagation()}>
                    <div className="action-buttons ts-action-btns">
                      <button className="btn btn-sm btn-primary ts-approve-btn" onClick={() => handleApprove(t.id, '')}
                        disabled={processing === t.id} title="Quick approve">
                        {processing === t.id ? '…' : '✓ Approve'}
                      </button>
                      <button className="btn btn-sm btn-secondary ts-view-btn" onClick={() => setSelected(t)} title="Open detail">
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailModal
        timesheet={selected}
        onClose={() => setSelected(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        processing={processing}
      />
    </div>
  );
}

export default TimesheetApprovals;
